import { deepPCMBMindmap } from "./data";
import { EXAM_SUBJECTS } from "./types";
import type { ExamId, Mindmap, MindmapNode, SubjectId } from "./types";

/** Pure lookups over the supplied dataset. No academic content is derived here. */

const SUBJECT_ALIASES: Record<string, SubjectId> = {
  physics: "physics",
  chemistry: "chemistry",
  biology: "biology",
  math: "math",
  maths: "math",
  mathematics: "math",
};

const slug = (value: string) =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");

/** subject -> chapter mindmaps, flattened from the supplied tree. */
const BY_SUBJECT: Partial<Record<SubjectId, Mindmap[]>> = (() => {
  const out: Partial<Record<SubjectId, Mindmap[]>> = {};
  const root = deepPCMBMindmap as MindmapNode;
  for (const subjectNode of root.children ?? []) {
    const subject = SUBJECT_ALIASES[subjectNode.name.trim().toLowerCase()];
    if (!subject) continue;
    const maps: Mindmap[] = [];
    for (const group of subjectNode.children ?? []) {
      for (const chapter of group.children ?? []) {
        maps.push({
          id: `${subject}-${slug(group.name)}-${slug(chapter.name)}`,
          chapter: chapter.name,
          group: group.name,
          subject,
          nodes: chapter.children ?? [],
        });
      }
    }
    out[subject] = maps;
  }
  return out;
})();

export function getMindmaps(exam: ExamId, subject: SubjectId): Mindmap[] {
  if (!EXAM_SUBJECTS[exam].includes(subject)) return [];
  return BY_SUBJECT[subject] ?? [];
}

/** Subjects valid for the exam that actually have data. */
export function mindmapSubjects(exam: ExamId): SubjectId[] {
  return EXAM_SUBJECTS[exam].filter((s) => (BY_SUBJECT[s]?.length ?? 0) > 0);
}

export function hasMindmapData(): boolean {
  return mindmapSubjects("jee").length > 0 || mindmapSubjects("neet").length > 0;
}

const normalise = (value: string) => value.trim().toLowerCase();

export function searchMindmaps(list: Mindmap[], query: string): Mindmap[] {
  const q = normalise(query);
  if (!q) return list;
  return list.filter((m) => normalise(m.chapter).includes(q) || normalise(m.group).includes(q));
}

/** Distinct groups (e.g. "Class 11 Physics") present in a list, in dataset order. */
export function mindmapGroups(list: Mindmap[]): string[] {
  return [...new Set(list.map((m) => m.group))];
}

export function countBranches(map: Mindmap): number {
  const walk = (nodes: MindmapNode[]): number =>
    nodes.reduce((sum, n) => sum + 1 + walk(n.children ?? []), 0);
  return walk(map.nodes);
}
